/**
 * The Kassabuch of a till: every movement on its Sachkonto - Barbelege paid in cash, money taken
 * from or brought to the bank, movements typed in by hand - with the running balance, read from
 * the journal. A till cannot hold less than nothing; the page says on which day it would.
 */
import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { BelegNumber } from "@/components/documents/document-badges";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateManualTransaction, useJournalLines } from "@/hooks/use-banking";
import type { BankAccount } from "@/lib/api/banking";
import { cashBook } from "@/lib/banking/money";
import { parseExportSettings } from "@/lib/export/settings";
import { moneyAccount } from "@/lib/ledger/money";
import { useWorkspace } from "@/lib/session/workspace-context";
import { cn } from "@/lib/utils/cn";
import { formatDate, formatMoney, parseDecimal } from "@/lib/utils/format";

export function CashBook({ account, canWrite }: { account: BankAccount; canWrite: boolean }) {
  const { t } = useTranslation();
  const { workspace } = useWorkspace();
  const ledgerAccount = moneyAccount(
    account,
    parseExportSettings(workspace?.accountingExport).cashAccount,
  );
  const { data: lines } = useJournalLines(workspace?.id, ledgerAccount);
  const [entering, setEntering] = useState(false);
  const book = cashBook(lines ?? []);

  return (
    <section className="space-y-3" data-testid="cash-book">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-medium">{t("cashBook.title")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("cashBook.subtitle", { account: ledgerAccount })}
          </p>
        </div>
        {canWrite && (
          <Button variant="outline" onClick={() => setEntering(true)} data-testid="new-cash-entry">
            <Plus /> {t("cashBook.enter")}
          </Button>
        )}
      </div>

      {book.negativeOn && (
        <Alert variant="destructive" data-testid="cash-negative">
          <AlertTitle>{t("cashBook.negativeTitle")}</AlertTitle>
          <AlertDescription>
            {t("cashBook.negativeText", { date: formatDate(book.negativeOn) })}
          </AlertDescription>
        </Alert>
      )}

      {book.rows.length === 0 ? (
        <p className="rounded-lg border px-4 py-6 text-center text-sm text-muted-foreground">
          {t("cashBook.empty")}
        </p>
      ) : (
        <ul className="divide-y rounded-lg border bg-card" data-testid="cash-book-rows">
          {book.rows.map((row) => (
            <li
              key={row.id}
              className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 px-4 py-2.5 text-sm sm:grid-cols-[6rem_minmax(0,1fr)_7rem_7rem]"
              data-testid="cash-book-row"
            >
              <span className="col-start-1 row-start-2 text-xs text-muted-foreground num sm:row-start-1 sm:text-sm">
                {formatDate(row.date)}
              </span>
              <span className="col-start-1 row-start-1 flex min-w-0 items-center gap-2 sm:col-start-2">
                {row.docNumber && <BelegNumber value={row.docNumber} />}
                <span className="truncate">{row.text || "—"}</span>
              </span>
              <span
                className={cn(
                  "col-start-2 row-start-1 text-right font-medium num sm:col-start-3",
                  row.amount > 0 && "text-success",
                )}
              >
                {formatMoney(row.amount)}
              </span>
              <span
                className="col-start-2 row-start-2 text-right text-xs text-muted-foreground num sm:col-start-4 sm:row-start-1 sm:text-sm"
                data-testid="cash-balance"
              >
                {formatMoney(row.balance)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <CashEntryDialog account={account} open={entering} onOpenChange={setEntering} />
    </section>
  );
}

function CashEntryDialog({
  account,
  open,
  onOpenChange,
}: {
  account: BankAccount;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const { workspace } = useWorkspace();
  const create = useCreateManualTransaction(workspace?.id ?? "");
  const [direction, setDirection] = useState<"out" | "in">("out");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [text, setText] = useState("");
  const value = parseDecimal(amount);

  async function save() {
    if (!value) return;
    await create.mutateAsync({
      accountId: account.id,
      entry: {
        date,
        amount: direction === "out" ? -Math.abs(value) : Math.abs(value),
        counterparty: text.trim(),
      },
    });
    toast.success(t("cashBook.saved"));
    setAmount("");
    setText("");
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("cashBook.enter")}</DialogTitle>
          <DialogDescription>{t("cashBook.enterHint")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4" data-testid="cash-entry-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="cash-direction">{t("cashBook.direction")}</Label>
              <Select value={direction} onValueChange={(next) => setDirection(next as never)}>
                <SelectTrigger id="cash-direction" data-testid="cash-direction">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="out">{t("cashBook.out")}</SelectItem>
                  <SelectItem value="in">{t("cashBook.in")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cash-date">{t("cashBook.date")}</Label>
              <Input
                id="cash-date"
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cash-amount">{t("cashBook.amount")}</Label>
            <Input
              id="cash-amount"
              inputMode="decimal"
              className="num sm:w-40"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cash-text">{t("cashBook.text")}</Label>
            <Input
              id="cash-text"
              maxLength={200}
              value={text}
              placeholder={t("cashBook.textPlaceholder")}
              onChange={(event) => setText(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => void save()}
            disabled={!value || !date || !text.trim() || create.isPending}
            data-testid="save-cash-entry"
          >
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
